<template>
  <div>
    <LoadingContainer v-if="loading"/>

    <div v-else class="container">
      <div class="row">
        <div class="col-xs-6">
          <HelpText>
            <h3>
              Create Ad
            </h3>

            <p>
              Enter the details of the ad.
            </p>
          </HelpText>
        </div>

        <div class="col-xs-18">
          <form @submit.prevent="submit">
            <div class="panel panel-default">
              <div class="panel-body form-horizontal">
                <SelectFormGroup
                    v-model="portalId"
                    name="portal_id"
                    label="Portal"
                    help-text="The portal that the ad should be placed on."
                    :options="portalOptions"
                />

                <SelectFormGroup
                    v-model="channel"
                    name="channel"
                    label="Channel"
                    help-text="The channel that the ad should be placed on."
                    :options="channelOptions"
                    :disabled="portalId === null"
                />

                <div v-if="channelType === 'time-based-channels'">
                  <SelectFormGroup
                      v-model="ad.time_based_ad_format_id"
                      name="time_based_ad_format_id"
                      label="Ad Format"
                      help-text="The ad format."
                      :error="getError('time_based_ad_format_id')"
                      :options="timeBasedAdFormatOptions"
                  />

                  <TableFormGroup
                      v-model="selectedTimeBasedAdLocations"
                      name="time_based_ad_locations"
                      label="Locations"
                      help-text="The locations that the ad should be placed in."
                      :error="getError('time_based_ad_locations')"
                      :options="timeBasedAdLocationOptions"
                      option-label="Location"
                  />

                  <DateFormGroup
                      v-model="ad.starts_at"
                      name="starts_at"
                      label="Starts At"
                      help-text="The day that the ad starts."
                      :error="getError('starts_at')"
                      :parameters="dateParameters"
                      format="DD.MM.YYYY"
                  />

                  <DateFormGroup
                      v-model="ad.ends_at"
                      name="ends_at"
                      label="Ends At"
                      help-text="The day that the ad ends."
                      :error="getError('ends_at')"
                      :parameters="dateParameters"
                      format="DD.MM.YYYY"
                  />

                  <DateFormGroup
                      v-model="ad.content_deadline_at"
                      name="content_deadline_at"
                      label="Content Deadline At"
                      help-text="The deadline for receiving content for the ad."
                      :error="getError('content_deadline_at')"
                      :disabled="true"
                      format="DD.MM.YYYY"
                  />

                  <DateFormGroup
                      v-model="ad.content_deadline_reminder_at"
                      name="content_deadline_reminder_at"
                      label="Content Deadline Reminder At"
                      help-text="The date that the reminders for submitting ad content should be sent out."
                      :error="getError('content_deadline_reminder_at')"
                      :disabled="true"
                      format="DD.MM.YYYY"
                  />

                  <SelectFormGroup
                      v-model="ad.project_id"
                      name="project_id"
                      label="Project"
                      help-text="The project that the ad belongs to."
                      :options="projectOptions"
                      :nullable="true"
                  />

                  <NumberFormGroup
                      v-model="ad.price"
                      name="price"
                      label="Price"
                      help-text="The price of the ad."
                      :error="getError('price')"
                  />

                  <TextFormGroup
                      v-model="ad.url"
                      name="url"
                      label="URL"
                      help-text="The URL that the ad should point at (optional)."
                      :error="getError('url')"
                  />

                  <TextFormGroup
                      v-model="ad.content"
                      name="content"
                      label="Material Number"
                      help-text="The material number for the ad (optional)."
                      :error="getError('content')"
                  />

                  <TextFormGroup
                      v-model="ad.dtp_comment"
                      name="dtp_comment"
                      label="DTP Comment"
                      help-text="The comment for the DTP (optional)."
                      :error="getError('dtp_comment')"
                  />

                  <TextFormGroup
                      v-model="ad.customer_comment"
                      name="customer_comment"
                      label="Customer Comment"
                      help-text="The comment for the customer (optional)."
                      :error="getError('customer_comment')"
                  />
                </div>

                <div v-if="channelType === 'publication-based-channels'">
                  <SelectFormGroup
                      v-model="ad.publication_based_ad_format_id"
                      name="publication_based_ad_format_id"
                      label="Ad Format"
                      help-text="The ad format."
                      :error="getError('publication_based_ad_format_id')"
                      :options="publicationBasedAdFormatOptions"
                  />

                  <SelectFormGroup
                      v-model="ad.publication_based_ad_location_id"
                      name="publication_based_ad_location_id"
                      label="Ad Location"
                      help-text="The ad location."
                      :error="getError('publication_based_ad_location_id')"
                      :options="publicationBasedAdLocationOptions"
                      :nullable="true"
                  />

                  <PublicationsFormGroup
                      v-model="ad.publications"
                      name="publications"
                      label="Publications"
                      help-text="The publications to include the ad in."
                      :error="getError('publications')"
                      :publication-based-channel-id="channelId"
                  />

                  <SelectFormGroup
                      v-model="ad.project_id"
                      name="project_id"
                      label="Project"
                      help-text="The project that the ad belongs to."
                      :options="projectOptions"
                      :nullable="true"
                  />

                  <NumberFormGroup
                      v-model="ad.price"
                      name="price"
                      label="Price"
                      help-text="The unit price of the ads."
                      :error="getError('price')"
                  />

                  <TextFormGroup
                      v-model="ad.url"
                      name="url"
                      label="URL"
                      help-text="The URL that the ad should point at (optional)."
                      :error="getError('url')"
                  />

                  <TextFormGroup
                      v-model="ad.content"
                      name="content"
                      label="Material Number"
                      help-text="The material number for the ad (optional)."
                      :error="getError('content')"
                  />

                  <TextFormGroup
                      v-model="ad.dtp_comment"
                      name="dtp_comment"
                      label="DTP Comment"
                      help-text="The comment for the DTP (optional)."
                      :error="getError('dtp_comment')"
                  />

                  <TextFormGroup
                      v-model="ad.customer_comment"
                      name="customer_comment"
                      label="Customer Comment"
                      help-text="The comment for the customer (optional)."
                      :error="getError('customer_comment')"
                  />
                </div>

                <hr>

                <SubmitFormGroup button-class="btn-black"/>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import http from '@/http'
import moment from 'moment'
import notify from '@/notify'
import DateFormGroup from '@/vendor/forms/FormGroups/Horizontal/Date'
import Errors from '@/mixins/Errors'
import HelpText from '@/components/HelpText'
import LoadingContainer from '@/components/Layout/LoadingContainer'
import NumberFormGroup from '@/vendor/forms/FormGroups/Horizontal/Number'
import PublicationsFormGroup from '@/components/FormGroups/Horizontal/Publications'
import SelectFormGroup from '@/vendor/forms/FormGroups/Horizontal/Select'
import SubmitFormGroup from '@/vendor/forms/FormGroups/Horizontal/Submit'
import TableFormGroup from '@/components/FormGroups/Horizontal/Table'
import TextFormGroup from '@/vendor/forms/FormGroups/Horizontal/Text'

export default {
  components: {
    DateFormGroup,
    HelpText,
    LoadingContainer,
    NumberFormGroup,
    PublicationsFormGroup,
    SelectFormGroup,
    SubmitFormGroup,
    TableFormGroup,
    TextFormGroup
  },

  mixins: [Errors],

  model: {
    prop: 'company',
    event: 'company-updated'
  },

  props: ['company', 'orderId'],

  data() {
    return {
      ad: {
        type: null,
        order_id: this.orderId,
        starts_at: null,
        ends_at: null,
        content_deadline_at: null,
        publications: []
      },
      portalId: null,
      channel: null,
      portals: [],
      channels: [],
      timeBasedAdFormats: [],
      publicationBasedAdFormats: [],
      publicationBasedAdLocations: [],
      selectedTimeBasedAdLocations: [],
      loading: true
    }
  },

  watch: {
    channel() {
      this.ad.type = this.adType
    },

    'ad.publication_based_ad_format_id'(value) {
      this.ad.price = this.publicationBasedAdFormats.find(existing => existing.id === parseInt(value)).attributes.price
    },

    'ad.time_based_ad_format_id'() {
      this.calculateTimeBasedPrice()
    },

    'ad.starts_at'(value) {
      this.calculateTimeBasedPrice()

      this.ad.content_deadline_at = moment(value)
          .subtract(this.selectedChannel.default_content_deadline_period, 'days')
          .format('YYYY-MM-DD')
    },

    'ad.ends_at'() {
      this.calculateTimeBasedPrice()
    },

    'ad.content_deadline_at'(value) {
      this.ad.content_deadline_reminder_at = moment(value)
          .subtract(this.selectedChannel.default_content_deadline_reminder_period, 'days')
          .format('YYYY-MM-DD')
    },

    portalId() {
      this.ad.project_id = null
    }
  },

  mounted() {
    this.load()
  },

  computed: {
    portalOptions() {
      return this.portals.map(portal => {
        return {
          value: portal.id,
          label: portal.attributes.name
        }
      })
    },

    projectOptions() {
      return this.projects
          .filter(project => project.attributes.portal_id == this.portalId)
          .map(project => ({value: project.id, label: project.attributes.portal_name + ' - ' + project.attributes.project_category_name + ' - ' + project.attributes.name}))
    },

    channelOptions() {
      return this.channels
          .filter(channel => channel.portal_id == this.portalId)
          .map(channel => {
            return {
              value: JSON.stringify({id: channel.id, type: channel.type}),
              label: channel.name
            }
          })
    },

    timeBasedAdFormatOptions() {
      return this.timeBasedAdFormats
          .filter(timeBasedAdFormat => timeBasedAdFormat.attributes.time_based_channel_id == this.channelId)
          .map(timeBasedAdFormat => {
            return {
              value: timeBasedAdFormat.id,
              label: timeBasedAdFormat.attributes.name
            }
          })
    },

    timeBasedAdLocationOptions() {
      return this.timeBasedAdLocations
          .filter(timeBasedAdLocation => timeBasedAdLocation.attributes.time_based_channel_id == this.channelId)
          .map(timeBasedAdLocation => {
            return {
              value: timeBasedAdLocation.id,
              label: timeBasedAdLocation.attributes.name
            }
          })
    },

    publicationBasedAdFormatOptions() {
      return this.publicationBasedAdFormats
          .filter(publicationBasedAdFormat => publicationBasedAdFormat.attributes.publication_based_channel_id == this.channelId)
          .map(publicationBasedAdFormat => {
            return {
              value: publicationBasedAdFormat.id,
              label: publicationBasedAdFormat.attributes.name
            }
          })
    },

    publicationBasedAdLocationOptions() {
      return this.publicationBasedAdLocations
          .filter(publicationBasedAdLocation => publicationBasedAdLocation.attributes.publication_based_channel_id == this.channelId)
          .map(publicationBasedAdLocation => {
            return {
              value: publicationBasedAdLocation.id,
              label: publicationBasedAdLocation.attributes.name
            }
          })
    },

    channelType() {
      return this.channel === null ? null : JSON.parse(this.channel).type
    },

    channelId() {
      return this.channel === null ? null : JSON.parse(this.channel).id
    },

    adType() {
      if (this.channelType === null) {
        return null
      }

      let types = {
        'time-based-channels': 'time-based-ads',
        'publication-based-channels': 'publication-based-ads'
      }

      return types[this.channelType]
    },

    selectedChannel() {
      return this.channels.find(channel => channel.type === this.channelType && channel.id === this.channelId)
    },

    dateParameters() {
      return {minDate: moment()}
    }
  },

  methods: {
    load() {
      Promise.all([
        http.getList('/api/v2/portals'),
        http.get('/api/v1/channels'),
        http.getList('/api/v2/time-based-ad-formats'),
        http.getList('/api/v2/publication-based-ad-formats'),
        http.getList('/api/v2/time-based-ad-locations'),
        http.getList('/api/v2/publication-based-ad-locations'),
        http.getList('/api/v2/projects', {'filter[status]': 'active'}),
      ]).then(responses => {
        this.portals = responses[0]
        this.channels = responses[1].data.data
        this.timeBasedAdFormats = responses[2]
        this.publicationBasedAdFormats = responses[3]
        this.timeBasedAdLocations = responses[4]
        this.publicationBasedAdLocations = responses[5]
        this.projects = responses[6]

        this.loading = false
      })
    },

    submit() {
      if (this.portalId === null) {
        return notify.error('Select a portal')
      }

      if (this.channel === null) {
        return notify.error('Select a channel')
      }

      http.post('/api/v1/ads', this.getRequestData())
          .then(() => {
            notify.success('The ad was saved')

            http.get('/api/v1/companies/' + this.company.data.id)
                .then(response => {
                  this.$emit('company-updated', response.data)

                  this.$router.push('..')
                })
          })
          .catch(this.catchValidationErrors)
    },

    getRequestData() {
      if (this.channelType === 'publication-based-ads') {
        return {
          ...this.ad,
          agency_commission: 0,
          third_party_costs: 0
        }
      }

      return {
        ...this.ad,
        agency_commission: 0,
        third_party_costs: 0,
        time_based_ad_locations: this.selectedTimeBasedAdLocations.map(timeBasedAdLocation => {
          return {id: timeBasedAdLocation}
        })
      }
    },

    calculateTimeBasedPrice() {
      if (!this.ad.starts_at || !this.ad.ends_at || !this.ad.time_based_ad_format_id) {
        return
      }

      let numberOfDays = Math.abs(moment(this.ad.starts_at).diff(this.ad.ends_at, 'days')) + 1

      let timeBasedAdFormat = this.timeBasedAdFormats.find(existing => {
        return existing.id === parseInt(this.ad.time_based_ad_format_id)
      })

      this.ad.price = numberOfDays * timeBasedAdFormat.attributes.price_per_day
    }
  }
}
</script>
